import React, { useEffect, useMemo, useState } from "react";
import AdminSidebar from "./AdminSidebar";
import { getAdminExams, getSections, addSection, updateSection, deleteSection } from "../api/adminclient";
import { apiErrorMessage } from "../api/auth";
import AdminTopbar from "./Admintopbar";

const emptyForm = { name: "", type: "MCQ", questions: "", marks: "", durationMinutes: "" };
const PAGE_SIZE = 5;

export default function AdminSections() {
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);

  useEffect(() => {
    getAdminExams()
      .then(({ data }) => {
        setExams(data);
        if (data.length > 0) setExamId(data[0].id);
      })
      .catch((err) => setError(apiErrorMessage(err, "Could not load exams.")));
  }, []);

  const loadSections = (id) => {
    if (!id) return;
    setLoading(true);
    getSections(id)
      .then(({ data }) => setSections(data))
      .catch((err) => setError(apiErrorMessage(err, "Could not load sections.")))
      .finally(() => setLoading(false));
  };

  useEffect(() => loadSections(examId), [examId]);

  // reset to page 1 whenever the selected exam changes
  useEffect(() => setPage(1), [examId]);

  const currentExam = exams.find((e) => String(e.id) === String(examId));

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (section) => {
    setEditingId(section.id);
    setForm({
      name: section.name,
      type: section.type,
      questions: section.questions,
      marks: section.marks,
      durationMinutes: section.durationMinutes,
    });
    setFormError("");
    setShowForm(true);
  };

  const save = async () => {
    setFormError("");
    if (!form.name || !form.questions || !form.durationMinutes) {
      setFormError("Section name, question count, and duration are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, examId };
      if (editingId) {
        await updateSection(editingId, payload);
      } else {
        await addSection(payload);
      }
      setShowForm(false);
      loadSections(examId);
    } catch (err) {
      setFormError(apiErrorMessage(err, "Could not save section."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this section?")) return;
    try {
      await deleteSection(id);
      loadSections(examId);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not delete section."));
    }
  };

  // ---- Pagination ----
  const totalPages = Math.max(1, Math.ceil(sections.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedSections = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sections.slice(start, start + PAGE_SIZE);
  }, [sections, page]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />
      <div className="admin-content">
        <AdminTopbar/>
        <h2 className="h5 fw-bold mb-3">Exam Sections</h2>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}

        <div className="admin-card p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <label className="small text-muted mb-0">Exam:</label>
              <select className="form-select form-select-sm" style={{ width: 220 }} value={examId} onChange={(e) => setExamId(e.target.value)}>
                {exams.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
              {currentExam && <h3 className="h6 fw-bold mb-0 ms-2">{currentExam.name} - Sections</h3>}
            </div>
            <button className="btn btn-primary btn-sm" onClick={openAdd} disabled={!examId}>
              <i className="bi bi-plus-lg me-1" /> Add Section
            </button>
          </div>

          {loading && <p className="text-muted mb-0">Loading sections...</p>}
          {!loading && sections.length === 0 && <p className="text-muted small mb-0">No sections for this exam yet.</p>}

          {!loading && sections.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Section Name</th>
                      <th>Type</th>
                      <th>Questions</th>
                      <th>Marks</th>
                      <th>Duration</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSections.map((s, i) => (
                      <tr key={s.id}>
                        <td className="text-muted">{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td className="fw-semibold">{s.name}</td>
                        <td>{s.type}</td>
                        <td>{s.questions}</td>
                        <td>{s.marks}</td>
                        <td>{s.durationMinutes} min</td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(s)}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(s.id)}>
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted small">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sections.length)} of {sections.length}
                </span>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => goToPage(page - 1)}>
                        <i className="bi bi-chevron-left" />
                      </button>
                    </li>
                    {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((p) => (
                      <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
                        <button className="page-link" onClick={() => goToPage(p)}>{p}</button>
                      </li>
                    ))}
                    <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => goToPage(page + 1)}>
                        <i className="bi bi-chevron-right" />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>

      {showForm && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? "Edit Section" : "Add Section"}</h5>
                <button className="btn-close" onClick={() => setShowForm(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small text-muted">Section Name</label>
                  <input className="form-control" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small text-muted">Type</label>
                    <select className="form-select" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                      <option>MCQ</option>
                      <option>Essay</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted">Duration (min)</label>
                    <input type="number" className="form-control" value={form.durationMinutes} onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))} />
                  </div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label small text-muted">Questions</label>
                    <input type="number" className="form-control" value={form.questions} onChange={(e) => setForm((f) => ({ ...f, questions: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted">Marks per Question</label>
                    <input type="number" className="form-control" value={form.marks} onChange={(e) => setForm((f) => ({ ...f, marks: e.target.value }))} />
                  </div>
                </div>
                {formError && <div className="text-danger small mt-2">{formError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}